import { hydrateRoot, createRoot } from "react-dom/client";
import { queryClient } from "./lib/queryClient";
import { preloadRouteForPath } from "./lib/route-imports";
import "./index.css";
import { initRedditPixel } from "./lib/reddit-pixel";
import { initOpenAIPixel } from "./lib/openai-pixel";
import { initMicrosoftUet } from "./lib/microsoft-uet";
import { applySavedConsent } from "./lib/consent";
import type { SsrQueryData } from "@shared/ssr-query-data";
import { markServerRenderedHead } from "./lib/route-head";

// A server-rendered page arrives with its head already resolved by getSeo;
// the router must not rewrite it on first load (see ScrollToTop in App).
if (document.getElementById("root")?.hasChildNodes()) {
  markServerRenderedHead(window.location.pathname + window.location.search);
}

// Keep the server-visible consent cookie in sync before any tracking runs.
try { applySavedConsent(); } catch { /* storage unavailable */ }
// Ad pixels: each init installs its queue stub now (so events fired during
// hydration are kept); the library itself downloads on the shared
// marketing-load trigger in index.html (first interaction or 4 s after load).
// Reddit Pixel.
initRedditPixel();
// ChatGPT Ads Measurement Pixel (live site only).
initOpenAIPixel();
// Microsoft Advertising UET tag (live site only; tracks page views itself).
initMicrosoftUet();

const ssrProductsEl = document.getElementById("__SSR_PRODUCTS__");
if (ssrProductsEl) {
  try {
    const productsData = JSON.parse(ssrProductsEl.textContent || "");
    queryClient.setQueryData(["/api/products"], productsData);
  } catch {}
  ssrProductsEl.remove();
}

const ssrServicesEl = document.getElementById("__SSR_SERVICES__");
if (ssrServicesEl) {
  try {
    const servicesData = JSON.parse(ssrServicesEl.textContent || "");
    queryClient.setQueryData(["/api/services"], servicesData);
  } catch {}
  ssrServicesEl.remove();
}

const ssrReviewStatsEl = document.getElementById("__SSR_REVIEW_STATS__");
if (ssrReviewStatsEl) {
  try {
    const statsData = JSON.parse(ssrReviewStatsEl.textContent || "");
    if (statsData) queryClient.setQueryData(["/api/reviews/stats"], statsData);
  } catch {}
  ssrReviewStatsEl.remove();
}

const ssrQueriesEl = document.getElementById("__SSR_QUERIES__");
if (ssrQueriesEl) {
  try {
    const entries: SsrQueryData[] = JSON.parse(ssrQueriesEl.textContent || "");
    for (const { queryKey, data } of entries) queryClient.setQueryData(queryKey, data);
  } catch (error) {
    console.error("Unable to restore public server-rendered data:", error);
  }
  ssrQueriesEl.remove();
}

async function hydrate() {
  const routePreload = preloadRouteForPath(window.location.pathname);
  if (routePreload) {
    await routePreload;
  }

  const { default: App } = await import("./App");

  const root = document.getElementById("root")!;
  root.classList.remove("ssr-ready");
  const ssrStyles = document.getElementById("ssr-overrides");
  if (ssrStyles) ssrStyles.remove();
  // If the server skipped SSR (e.g. admin routes), root is empty — do a fresh
  // render instead of hydrating to avoid React hydration mismatch errors.
  if (root.hasChildNodes()) {
    hydrateRoot(root, <App />);
  } else {
    createRoot(root).render(<App />);
  }
}

hydrate();
